import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Media library modal — lists all uploaded images, allows insert & delete.
 *  Props:
 *    - onClose(): dismiss
 *    - onInsert(url, filename): fired when user clicks "Insert"; parent inserts into the editor.
 */
export default function MediaLibrary({ onClose, onInsert }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/ph360/media/list"); setItems(data || []); }
    catch (err) { toast.error(formatApiError(err)); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/ph360/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Uploaded");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const del = async (fileId) => {
    if (!window.confirm("Delete this image? It stays available for anyone with old links until you replace them.")) return;
    setBusy(true);
    try {
      await api.delete(`/ph360/media/file/${fileId}`);
      setItems(items.filter(i => i.file_id !== fileId));
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const insert = (item) => {
    const abs = item.url.startsWith("http") ? item.url : `${backendBase}${item.url}`;
    onInsert?.(abs, item.original_filename || "");
    onClose?.();
  };

  const copyUrl = async (item) => {
    const abs = item.url.startsWith("http") ? item.url : `${backendBase}${item.url}`;
    try { await navigator.clipboard.writeText(abs); toast.success("URL copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="ef360-modal" data-testid="media-library-modal" onClick={onClose}>
      <div className="ef360-modal-body" onClick={(e)=>e.stopPropagation()} style={{maxWidth:900}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          <h3 style={{margin:0}}>Media Library</h3>
          <button className="button" onClick={onClose} data-testid="media-library-close">Close</button>
        </div>
        <p className="description" style={{marginTop:6}}>
          Reuse or delete uploaded images. Deleted images stop rendering for anyone.
        </p>
        <div style={{display:"flex", gap:8, alignItems:"center", marginTop:6}}>
          <button className="button button-primary" onClick={()=>fileInputRef.current?.click()} disabled={uploading} data-testid="media-library-upload">
            {uploading ? "Uploading…" : "⬆ Upload new image"}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={upload} data-testid="media-library-file-input"/>
          <span className="description">JPG · PNG · WEBP · GIF (max 8MB)</span>
        </div>
        <div style={{
          marginTop:14,
          display:"grid",
          gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))",
          gap:12, maxHeight:"56vh", overflowY:"auto",
        }} data-testid="media-library-grid">
          {items === null && <p className="description">Loading…</p>}
          {items && items.length === 0 && <p className="description">No uploads yet.</p>}
          {items && items.map(it => {
            const abs = it.url.startsWith("http") ? it.url : `${backendBase}${it.url}`;
            return (
              <div key={it.file_id} className="ef360-media-card" data-testid={`media-item-${it.file_id}`}
                   style={{border:"1px solid #dcdcde", background:"#fff", padding:8}}>
                <div style={{width:"100%", aspectRatio:"1/1", background:"#f6f7f7", display:"grid", placeItems:"center", overflow:"hidden"}}>
                  <img src={abs} alt={it.original_filename || ""} style={{maxWidth:"100%", maxHeight:"100%", objectFit:"contain"}} />
                </div>
                <div title={it.original_filename} style={{
                  fontSize:11, marginTop:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                }}>{it.original_filename || it.file_id}</div>
                <div style={{display:"flex", gap:4, marginTop:6}}>
                  {onInsert && (
                    <button className="button" onClick={()=>insert(it)} data-testid={`media-item-insert-${it.file_id}`} style={{fontSize:11, padding:"2px 6px"}}>Insert</button>
                  )}
                  <button className="button" onClick={()=>copyUrl(it)} data-testid={`media-item-copy-${it.file_id}`} style={{fontSize:11, padding:"2px 6px"}}>URL</button>
                  <button className="button delete" onClick={()=>del(it.file_id)} disabled={busy} data-testid={`media-item-delete-${it.file_id}`} style={{fontSize:11, padding:"2px 6px"}}>Del</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
